import { library } from '@fortawesome/fontawesome-svg-core';
import { faChartBar, faFileCsv } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useTranslator } from '@Localization';
import { Button } from '@mui/joy';
import { TooltipItem, TooltipModel } from 'chart.js';
import { observer } from 'mobx-react-lite';
import { ReactElement } from 'react';
import { Bar } from 'react-chartjs-2';
import { QuestionType } from '../../../../annotation/question.model';
import { IAnswerStatisticDto } from '../projectDetailsStatistics.model';

library.add(faChartBar, faFileCsv);

const MAX_VISIBLE_LABEL = 40;

interface IAnnotationProgressChartProps {
  answerStatistics: IAnswerStatisticDto;
  onGetAnswers: () => Promise<void>;
}

export const AnswersChart = observer((props: IAnnotationProgressChartProps): ReactElement => {
  const { t } = useTranslator();
  const { answerStatistics, onGetAnswers } = props;

  const sorted = answerStatistics.answers.slice().sort((x, y) => x.order - y.order);
  const style = getComputedStyle(document.body);

  const attributeData = {
    labels: sorted.map((a) => a.text),
    datasets: [
      {
        label: t('awaiting_review'),
        data: sorted.map((a) => a.annotated),
        backgroundColor: style.getPropertyValue('--joy-palette-primary-500'),
        hoverBackgroundColor: style.getPropertyValue('--joy-palette-primary-500'),
      },
      {
        label: t('accepted'),
        data: sorted.map((a) => a.approved),
        backgroundColor: style.getPropertyValue('--joy-palette-success-500'),
        hoverBackgroundColor: style.getPropertyValue('--joy-palette-success-500'),
      },
      {
        label: t('rejected'),
        data: sorted.map((a) => a.rejected),
        backgroundColor: style.getPropertyValue('--joy-palette-danger-500'),
        hoverBackgroundColor: style.getPropertyValue('--joy-palette-danger-500'),
      },
    ],
  };

  const longestLabel = sorted.length ? Math.max(...sorted.map((x) => x.text.length)) : 0;
  const width = sorted.length * 30;
  const height = 330 + Math.min(longestLabel, MAX_VISIBLE_LABEL) * 5;

  return (
    <div className='statistic-wrapper'>
      <div className='statistic-wrapper-header'>{answerStatistics.text}</div>
      <div
        className='statistics-body'
        css={{ height: height + 20 }}
      >
        <div className='statistics-summary'>
          {answerStatistics.questionType === QuestionType.OPEN ? (
            <div>
              <Button
                onClick={() => {
                  void onGetAnswers();
                }}
                startDecorator={<FontAwesomeIcon icon={faFileCsv} />}
              >
                {t('all_results')}
              </Button>
            </div>
          ) : (
            <>
              {sorted.map((a) => (
                <div key={a.id}>
                  {a.text || t('answers')}: {a.annotated + a.approved + a.rejected}
                </div>
              ))}
            </>
          )}
        </div>
        <div className='chart-wrapper'>
          <div
            className='statistics-chart wide'
            css={{ width, height }}
          >
            <Bar
              data={attributeData}
              options={{
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    display: true,
                    position: 'right',
                    align: 'center',
                  },
                  tooltip: {
                    mode: 'index',
                    intersect: false,
                    callbacks: {
                      label(this: TooltipModel<'bar'>, tooltipItem: TooltipItem<'bar'>): string {
                        return `${tooltipItem.dataset.label}: ${tooltipItem.formattedValue}`;
                      },
                    },
                  },
                },
                scales: {
                  x: {
                    stacked: true,
                    grid: { display: false },
                  },
                  y: {
                    beginAtZero: true,
                    suggestedMin: 0,
                    stacked: true,
                  },
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
});
